import React, { useState, useRef, DragEvent } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Button } from "barte-design-system";
import { UploadSimpleIcon, TrashIcon } from "@phosphor-icons/react";
import * as XLSX from "xlsx";

import { ICompanyPlan } from '@shared/types';

import { ModalCompanyPlanUploadProps } from "./modal-company-plan-upload.type";

import { ACCEPTED_FILE_TYPES } from "./modal-company-plan-upload.constants";
import { buildTaxConfigs, formatBytes } from "./modal-company-plan-upload.helpers";
import styles from "./modal-company-plan-upload.module.css";

export const ModalCompanyPlanUpload = ({ isOpen, onClose, companyId, onHandleSubmit }: ModalCompanyPlanUploadProps) => {
    const { t } = useTranslation();
    const [files, setFiles] = useState<File[]>([]);
    const [isDragging, setIsDragging] = useState(false);
    const [isReading, setIsReading] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const selectedFiles = Array.from(e.target.files);
            setFiles(prev => [...prev, ...selectedFiles]);
        }
    };

    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const droppedFiles = Array.from(e.dataTransfer.files);
            setFiles(prev => [...prev, ...droppedFiles]);
        }
    };

    const handleContainerClick = () => {
        inputRef.current?.click();
    };

    const handleRemoveFile = (index: number) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleClose = () => {
        setFiles([]);
        if (inputRef.current) {
            inputRef.current.value = "";
        }
        onClose();
    };

    const readFileAsync = (file: File): Promise<any[]> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = (e) => {
                const data = e.target?.result;
                if (!data) {
                    resolve([]);
                    return;
                }

                try {
                    const workbook = XLSX.read(data, { type: "binary", raw: true });
                    const firstSheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[firstSheetName];
                    const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

                    const mappedData = jsonData.map((row: any) => ({
                        mcc: row["mcc"] || row["MCC"],
                        method: row["metodo"] || row["método"] || row["method"],
                        capture: row["captura"] || row["capture"],
                        network: row["bandeira"] || row["network"],
                        statement: row["parcela"] || row["statement"],
                        settlementDays: row["settlement_days"],
                        percentualRate: row["taxa_percentual"] || row["percentual_rate"],
                        rateType: row["tipo de taxa"] || row["rate_type"],
                        rateLimit: row["limite de taxas"] || row["rate_limit"],
                        defaultDay: row["default dia"] || row["default_day"],
                        defaultBrand: row["default bandeira"] || row["default_brand"]
                    }));

                    resolve(mappedData);
                } catch (err) {
                    reject(err);
                }
            };

            reader.onerror = (err) => reject(err);
            reader.readAsBinaryString(file);
        });
    };

    const handleSubmit = async () => {
        if (files.length === 0) return;
        setIsReading(true);

        try {
            const allFilesData = await Promise.all(files.map(file => readFileAsync(file)));
            const combinedMappedData = allFilesData.flat();

            const taxConfigsByMcc = buildTaxConfigs(combinedMappedData);

            const objectExtract: Partial<ICompanyPlan> = {
                idCompanies: companyId ? [+companyId] : [],
                name: files.length === 1 ? files[0].name : "Planilha Importada",
                tax: { taxConfigsByMcc },
                id: undefined
            };

            onHandleSubmit(objectExtract);
            handleClose();
        } catch (err) {
            console.error("Error reading files:", err);
        } finally {
            setIsReading(false);
        }
    };

    const getFileExtension = (filename: string) => {
        const parts = filename.split('.');
        return parts.length > 1 ? parts[parts.length - 1] : '';
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={t('backoffice.pageModalCompanyPlanUploadTitle')}
            className={styles.modal}
            size="sm"
        >
            <div className={styles.description}>
                {t('backoffice.pageModalCompanyPlanUploadDescription')}
            </div>

            <div className={styles.content}>
                <div
                    className={`${styles['upload-area']} ${isDragging ? styles['upload-area-dragging'] : ""}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={handleContainerClick}
                >
                    <input
                        type="file"
                        ref={inputRef}
                        onChange={handleFileChange}
                        className={styles['hidden-input']}
                        accept={ACCEPTED_FILE_TYPES}
                        multiple
                    />

                    <div className={styles['icon-wrapper']}>
                        <UploadSimpleIcon size={24} />
                    </div>

                    <div className={styles['upload-text']}>
                        <div className={styles.title}>
                            <span className={styles.link}>{t('backoffice.pageModalCompanyPlanUploadClickToUpload')}</span> {t('backoffice.pageModalCompanyPlanUploadOrDragAndDrop')}
                        </div>
                        <div className={styles.subtitle}>
                            {t('backoffice.pageModalCompanyPlanUploadSupportedFiles')}
                        </div>
                    </div>
                </div>

                {files.length > 0 && (
                    <div className={styles['file-list']}>
                        {files.map((file, index) => (
                            <div key={`${file.name}-${index}`} className={styles['file-item']}>
                                <div className={styles['file-icon']}>
                                    {getFileExtension(file.name)}
                                </div>
                                <div className={styles['file-info']}>
                                    <span className={styles['file-name-label']}>{file.name}</span>
                                    <span className={styles['file-size-label']}>{formatBytes(file.size)}</span>
                                </div>
                                <div
                                    className={styles['remove-file-button']}
                                    onClick={() => handleRemoveFile(index)}
                                    title={t('backoffice.pageModalCompanyPlanUploadRemove')}
                                >
                                    <TrashIcon size={20} />
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <div className={styles.footer}>
                    <Button variant="outline" onClick={handleClose} className={styles['footer-button']} disabled={isReading}>{t('backoffice.pageModalCompanyPlanUploadCancel')}</Button>
                    <Button disabled={files.length === 0 || isReading} onClick={handleSubmit} className={styles['footer-button']} loading={isReading}>{t('backoffice.pageModalCompanyPlanUploadProceed')}</Button>
                </div>
            </div>
        </Modal>
    );
};
